// Routes unrevealed badge copies to their reveal, worst to best: the tiles appear
// first, then the cards one by one, then the cinematics, so the best badge is the
// last and biggest moment. Copies of one badge are one reveal with their count.
// History copies go the same way, at the level their rarity gives them. A card or
// cinematic offers "Skip the other silver (4)" while more of its tier are queued.
// Presentational: the host feeds it the server's copies and turns the callbacks
// into API calls; the badge lab feeds it fake copies.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBadgeLib } from "../art";
import type { UnrevealedCopy } from "../types";
import BadgeCinematic from "./BadgeCinematic";
import BadgeRevealCard from "./BadgeRevealCard";
import BadgeTileStack from "./BadgeTileStack";
import { useFloatingChromeOpen, useReducedMotion, useTypingFocus } from "./hooks";
import { planReveals, skipTierLabel, type RevealGroup } from "./revealQueue";
import "./reveal.css";

export interface BadgeRevealViewProps {
  /** Unrevealed copies not dealt with yet, in any order. */
  items: UnrevealedCopy[];
  /** Hold back cinematics and cards (tiles still show). */
  paused?: boolean;
  /** Put the tiles above the siteline dock chip (desktop home). */
  aboveDock?: boolean;
  /** Pause between one big reveal closing (or the page settling) and the next opening, ms. */
  openDelayMs?: number;
  /** Collect: a reveal's Collect button (every copy it folds), and "Collect all" on the tiles. */
  onCollect: (copyIds: string[]) => void;
  /** A tile, or "See collection": open the badge in the collection and collect. */
  onOpenBadge: (code: string, copyIds: string[]) => void;
  /** Share: make the link, collect the reveal's copies and open the share page. Rejects when that failed. */
  onShare: (group: RevealGroup) => Promise<void>;
}

const CLOSE_MS = 260;

const BadgeRevealView: React.FC<BadgeRevealViewProps> = ({
  items,
  paused = false,
  aboveDock = false,
  openDelayMs = 700,
  onCollect,
  onOpenBadge,
  onShare,
}) => {
  const typing = useTypingFocus();
  const chromeOpen = useFloatingChromeOpen();
  const reduced = useReducedMotion();
  const { loading: libLoading } = useBadgeLib();
  const plan = useMemo(() => planReveals(items), [items]);

  // the card or cinematic on screen, by badge code
  const [activeCode, setActiveCode] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  const active = activeCode ? plan.groups.find((g) => g.code === activeCode && g.level !== "tile") ?? null : null;

  // the active badge was dealt with (or vanished from the server's list)
  useEffect(() => {
    if (activeCode && !active) {
      setActiveCode(null);
      setClosing(false);
    }
  }, [activeCode, active]);

  // open the next card or cinematic once nobody is typing and the art can draw
  const nextCode = plan.current?.code ?? null;
  const blocked = paused || typing || libLoading;
  useEffect(() => {
    if (active || !nextCode || blocked) return undefined;
    const t = window.setTimeout(() => setActiveCode(nextCode), openDelayMs);
    return () => window.clearTimeout(t);
  }, [active, nextCode, blocked, openDelayMs]);

  const closeTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  const collectActive = useCallback(() => {
    if (!active || closing) return;
    const ids = active.copyIds;
    setClosing(true);
    closeTimer.current = window.setTimeout(() => onCollect(ids), reduced ? 0 : CLOSE_MS);
  }, [active, closing, onCollect, reduced]);

  const seeActive = useCallback(() => {
    if (active) onOpenBadge(active.code, active.copyIds);
  }, [active, onOpenBadge]);

  const shareActive = useCallback(async () => {
    if (active) await onShare(active);
  }, [active, onShare]);

  // the other queued cards and cinematics of the active one's tier
  const sameTier = useMemo(
    () => (active ? plan.groups.filter((g) => g.level !== "tile" && g.code !== active.code && g.display.tier === active.display.tier) : []),
    [active, plan.groups]
  );

  // collect the active reveal and every queued one of its tier in one call; the
  // queue goes on with the next tier up
  const skipActiveTier = useCallback(() => {
    if (!active || closing || !sameTier.length) return;
    const ids = [...active.copyIds, ...sameTier.flatMap((g) => g.copyIds)];
    setClosing(true);
    closeTimer.current = window.setTimeout(() => onCollect(ids), reduced ? 0 : CLOSE_MS);
  }, [active, closing, sameTier, onCollect, reduced]);

  let big: React.ReactNode = null;
  if (active) {
    const waiting = Math.max(0, plan.groups.filter((g) => g.level !== "tile" && g.code !== active.code).length);
    const shared = {
      copy: active.display,
      waiting,
      closing,
      onCollect: collectActive,
      onShare: shareActive,
      onSeeCollection: seeActive,
      skipTier: sameTier.length ? { label: skipTierLabel(active.display.tier, sameTier.length), onSkip: skipActiveTier } : undefined,
    };
    big =
      active.level === "cinematic" ? (
        <BadgeCinematic key={active.code} {...shared} />
      ) : (
        <BadgeRevealCard key={active.code} {...shared} />
      );
  }

  // the tiles come first and stay while the cards and cinematics play over them
  return (
    <>
      <BadgeTileStack
        tiles={plan.tiles}
        stashed={chromeOpen}
        aboveDock={aboveDock}
        onOpenTile={(group) => onOpenBadge(group.code, group.copyIds)}
        onCollectAll={onCollect}
      />
      {big}
    </>
  );
};

export default BadgeRevealView;
